import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { useMaterialRequestPendingCountOptional } from "features/MaterialRequests/context/MaterialRequestPendingCountContext";
import { useReadyForBillingWorkOrderCountOptional } from "features/WorkOrders/context/ReadyForBillingWorkOrderCountContext";
import { useReadyForBillingPettyWorkCountOptional } from "features/PettyWorks/context/ReadyForBillingPettyWorkCountContext";
import { formatBadgeCount } from "utils/helpers";

function filterMenuByPermission(
  items: any[],
  can: (permission: string, policy?: string) => boolean,
): any[] {
  const stripPermission = (entry: any) => {
    const { permission: _p, ...rest } = entry;
    return rest;
  };

  const mapped = items.map((item) => {
    if (item.isHeader) return item;

    if (Array.isArray(item.subItems) && item.subItems.length > 0) {
      const subItems = item.subItems
        .map((s: any) => {
          if (Array.isArray(s.childItems) && s.childItems.length > 0) {
            const childItems = s.childItems
              .filter((c: any) => !c.permission || can(c.permission, c.policy))
              .map(stripPermission);
            if (childItems.length === 0) return null;
            return { ...stripPermission(s), childItems };
          }
          if (s.permission && !can(s.permission, s.policy)) return null;
          return stripPermission(s);
        })
        .filter(Boolean);
      if (subItems.length === 0) return null;
      const { permission: _p, ...rest } = item;
      return { ...rest, subItems };
    }

    if (item.permission && !can(item.permission)) return null;
    const { permission: _p, ...rest } = item;
    return rest;
  });

  const list = mapped.filter(Boolean) as any[];

  return list.filter((item, index) => {
    if (!item.isHeader) return true;
    for (let j = index + 1; j < list.length; j++) {
      if (list[j].isHeader) break;
      return true;
    }
    return false;
  });
}

export default function Navdata() {
  const history = useNavigate();
  const { canWithPolicy } = useAccess();

  const { count: pendingMaterialRequestCount } =
    useMaterialRequestPendingCountOptional();
  const { count: readyForBillingCount } =
    useReadyForBillingWorkOrderCountOptional();
  const { count: readyForBillingPettyWorkCount } =
    useReadyForBillingPettyWorkCountOptional();

  const [isDashboard, setIsDashboard] = useState(false);
  const [isPettyWork, setIsPettyWork] = useState(false);
  const [isWorkOrder, setIsWorkOrder] = useState(false);
  const [isUserManagement, setIsUserManagement] = useState(false);
  const [isEnquiryManagement, setIsEnquiryManagement] = useState(false);
  const [isQuotations, setIsQuotations] = useState(false);
  const [isHRManagement, setIsHRManagement] = useState(false);
  const [isReports, setIsReports] = useState(false);
  const [isClients, setIsClients] = useState(false);
  const [isInventoryManagement, setIsInventoryManagement] = useState(false);
  const [isAdvanceSettlements, setIsAdvanceSettlements] = useState(false);
  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  function updateIconSidebar(e: any) {
    if (e && e.target && e.target.getAttribute("sub-items")) {
      const ul: any = document.getElementById("two-column-menu");
      const iconItems = ul.querySelectorAll(".nav-icon.active");
      let activeIconItems = [...iconItems];
      activeIconItems.forEach((item) => {
        item.classList.remove("active");

        var id = item.getAttribute("sub-items");
        const getID = document.getElementById(id) as HTMLElement;
        if (getID) getID.classList.remove("show");
      });
    }
  }

  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");
    if (iscurrentState !== "Dashboard") {
      setIsDashboard(false);
    }

    if (iscurrentState !== "Clients") {
      setIsClients(false);
    }

    if (iscurrentState !== "Petty Work") {
      setIsPettyWork(false);
    }

    if (iscurrentState !== "Work Order") {
      setIsWorkOrder(false);
    }

    if (iscurrentState !== "User Management") {
      setIsUserManagement(false);
    }

    if (iscurrentState !== "Enquiry Management") {
      setIsEnquiryManagement(false);
    }

    if (iscurrentState !== "Quotations") {
      setIsQuotations(false);
    }

    if (iscurrentState !== "HR Management") {
      setIsHRManagement(false);
    }

    if (iscurrentState !== "Reports") {
      setIsReports(false);
    }

    if (iscurrentState !== "Inventory Management") {
      setIsInventoryManagement(false);
    }

    if (iscurrentState !== "Advance & Settlements") {
      setIsAdvanceSettlements(false);
    }
  }, [
    history,
    iscurrentState,
    isDashboard,
    isEnquiryManagement,
    isPettyWork,
    isWorkOrder,
    isClients,
    isUserManagement,
    isQuotations,
    isHRManagement,
    isReports,
    isInventoryManagement,
    isAdvanceSettlements,
  ]);

  const materialRequestPendingCount = Number(pendingMaterialRequestCount) || 0;

  const menuItems: any = [
    {
      label: "Overview",
      isHeader: true,
    },

    {
      id: "dashboard",
      label: "Dashboard",
      icon: "bx bxs-dashboard",
      link: "/dashboard",
      permission: Permissions.DASHBOARD.READ,
      stateVariables: isDashboard,
      click: function (e: any) {
        e.preventDefault();
        setIsDashboard(!isDashboard);
        setIscurrentState("Dashboard");
        updateIconSidebar(e);
      },
    },

    {
      label: "People & access",
      isHeader: true,
    },

    {
      id: "user-management",
      label: "User Management",
      icon: "bx bxs-group",
      link: "/#",
      click: function (e: any) {
        e.preventDefault();
        setIsUserManagement(!isUserManagement);
        setIscurrentState("User Management");
        updateIconSidebar(e);
      },
      stateVariables: isUserManagement,
      subItems: [
        {
          id: "users",
          label: "Users",
          link: "/user-management/users",
          parentId: "user-management",
          permission: Permissions.USER.READ,
        },

        {
          id: "rolesAndPermissions",
          label: "Roles & Permissions",
          link: "/user-management/roles-and-permissions",
          parentId: "user-management",
          permission: Permissions.ROLES_AND_PERMISSIONS.READ,
        },
      ],
    },

    {
      id: "hr-management",
      label: "HR Management",
      icon: "bx bxs-briefcase-alt-2",
      link: "/#",
      click: function (e: any) {
        e.preventDefault();
        setIsHRManagement(!isHRManagement);
        setIscurrentState("HR Management");
        updateIconSidebar(e);
      },
      stateVariables: isHRManagement,
      subItems: [
        {
          id: "hr-employees",
          label: "Employees",
          link: "/employees",
          parentId: "hr-management",
          permission: Permissions.EMPLOYEE.READ,
        },
        {
          id: "departments",
          label: "Departments",
          link: "/hr-management/departments",
          parentId: "hr-management",
          permission: Permissions.DEPARTMENT.READ,
        },
        {
          id: "designations",
          label: "Designations",
          link: "/hr-management/designations",
          parentId: "hr-management",
          permission: Permissions.DESIGNATION.READ,
        },
        {
          id: "employee-attendance-sheet",
          label: "Attendance",
          link: "/hr-management/attendance-sheet",
          parentId: "hr-management",
          permission: Permissions.EMPLOYEE_ATTENDANCE.READ,
        },
        {
          id: "leave-applications",
          label: "Leaves",
          link: "/hr-management/leave-applications",
          parentId: "hr-management",
          permission: Permissions.LEAVE_APPLICATION.READ,
        },
        {
          id: "holidays",
          label: "Holidays",
          link: "/hr-management/holidays",
          parentId: "hr-management",
          permission: Permissions.HOLIDAY.READ,
        },
      ],
    },

    {
      label: "Reports",
      isHeader: true,
    },

    {
      id: "reports",
      label: "Reports",
      icon: "bx bx-bar-chart-alt-2",
      link: "/#",
      click: function (e: any) {
        e.preventDefault();
        setIsReports(!isReports);
        setIscurrentState("Reports");
        updateIconSidebar(e);
      },
      stateVariables: isReports,
      subItems: [
        {
          id: "reports-attendance",
          label: "Attendance Report",
          link: "/reports/attendance",
          parentId: "reports",
          permission: Permissions.EMPLOYEE_ATTENDANCE.READ,
        },
        {
          id: "reports-work-orders",
          label: "Work Order Report",
          link: "/reports/work-orders",
          parentId: "reports",
          permission: Permissions.WORK_ORDER.READ,
        },
      ],
    },

    {
      label: "Sales & enquiries",
      isHeader: true,
    },

    {
      id: "enquiryManagement",
      label: "Enquiry Management",
      icon: "bx bx-clipboard",
      link: "/enquiry-management",
      permission: Permissions.ENQUIRY.READ,
      stateVariables: isEnquiryManagement,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Enquiry Management");
        updateIconSidebar(e);
      },
    },

    {
      id: "quotations",
      label: "Quotations",
      icon: "ri-bill-line",
      link: "/quotations",
      permission: Permissions.QUOTATION.READ,
      stateVariables: isQuotations,
      click: function (e: any) {
        e.preventDefault();
        setIsQuotations(!isQuotations);
        setIscurrentState("Quotations");
        updateIconSidebar(e);
      },
    },

    {
      label: "Operations & service",
      isHeader: true,
    },

    {
      id: "schedule",
      label: "Assignments",
      icon: "ri-calendar-line",
      link: "/schedule",
      permission: Permissions.WORK_ASSIGNMENT.READ,
    },

    {
      id: "pettyWorks",
      label: "Petty Works",
      icon: "ri-tools-line",
      link: "/petty-works",
      permission: Permissions.PETTY_WORK.READ,
      stateVariables: isPettyWork,
      ...(readyForBillingPettyWorkCount > 0
        ? {
            badgeColor: "danger",
            badgeName: formatBadgeCount(readyForBillingPettyWorkCount),
          }
        : {}),
      click: function (e: any) {
        e.preventDefault();
        setIsPettyWork(!isPettyWork);
        setIscurrentState("Petty Work");
        updateIconSidebar(e);
      },
    },

    {
      id: "workOrders",
      label: "Work Orders",
      icon: "ri-briefcase-2-line",
      link: "/work-orders",
      permission: Permissions.WORK_ORDER.READ,
      stateVariables: isWorkOrder,
      ...(readyForBillingCount > 0
        ? {
            badgeColor: "danger",
            badgeName: formatBadgeCount(readyForBillingCount),
          }
        : {}),
      click: function (e: any) {
        e.preventDefault();
        setIsWorkOrder(!isWorkOrder);
        setIscurrentState("Work Order");
        updateIconSidebar(e);
      },
    },

    {
      id: "amc",
      label: "AMC Management",
      icon: "ri-shake-hands-line",
      link: "/amc-management",
      permission: Permissions.AMC.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("AMC Management");
        updateIconSidebar(e);
      },
    },

    {
      label: "Inventory Management",
      isHeader: true,
    },

    // {
    //   id: "inventory-management",
    //   label: "Inventory",
    //   icon: "bx bx-package",
    //   link: "/#",
    //   click: function (e: any) {
    //     e.preventDefault();
    //     setIsInventoryManagement(!isInventoryManagement);
    //     setIscurrentState("Inventory Management");
    //     updateIconSidebar(e);
    //   },
    //   stateVariables: isInventoryManagement,
    //   subItems: [
    //     {
    //       id: "products",
    //       label: "Products",
    //       link: "/inventory-management/products",
    //       parentId: "inventory-management",
    //       permission: Permissions.PRODUCT.READ,
    //     },
    //     {
    //       id: "productCategories",
    //       label: "Product categories",
    //       link: "/inventory-management/product-categories",
    //       parentId: "inventory-management",
    //       permission: Permissions.PRODUCT.READ,
    //     },
    //     {
    //       id: "stockLocations",
    //       label: "Stock locations",
    //       link: "/inventory-management/stock-locations",
    //       parentId: "inventory-management",
    //       permission: Permissions.STOCK_LOCATION.READ,
    //     },
    //     {
    //       id: "stockMovements",
    //       label: "Stock movements",
    //       link: "/inventory-management/stock-movements",
    //       parentId: "inventory-management",
    //       permission: Permissions.STOCK_MOVEMENT.READ,
    //     },
    //   ],
    // },

    {
      id: "materialRequests",
      label: "Material Requests",
      icon: "ri-inbox-archive-line",
      link: "/inventory-management/material-requests",
      ...(materialRequestPendingCount > 0
        ? {
            badgeColor: "danger",
            badgeName: formatBadgeCount(materialRequestPendingCount),
          }
        : {}),
      permission: Permissions.MATERIAL_REQUEST.READ,
    },

    {
      id: "purchaseOrders",
      label: "Purchase Orders",
      icon: "ri-shopping-basket-2-line",
      link: "/inventory-management/purchase-orders",
      permission: Permissions.PURCHASE_ORDER.READ,
    },

    {
      id: "suppliers",
      label: "Suppliers",
      icon: "ri-store-2-line",
      link: "/inventory-management/suppliers",
      permission: Permissions.SUPPLIER.READ,
    },

    {
      label: "Finance & Accounts",
      isHeader: true,
    },

    {
      id: "finance-invoices",
      label: "Invoices",
      icon: "ri-file-list-3-line",
      link: "/finance/invoices",
      permission: Permissions.INVOICE.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Invoices");
        updateIconSidebar(e);
      },
    },

    {
      id: "finance-payments",
      label: "Payments",
      icon: "ri-bank-card-2-line",
      link: "/finance/payments",
      permission: Permissions.PAYMENT.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Payments");
        updateIconSidebar(e);
      },
    },

    {
      id: "finance-payroll",
      label: "Payroll",
      icon: "ri-money-dollar-box-line",
      link: "/finance/payroll",
      permission: Permissions.PAYROLL.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Payroll");
        updateIconSidebar(e);
      },
    },

    {
      id: "finance-payslips",
      label: "Payslips",
      icon: "ri-file-paper-2-line",
      link: "/finance/payslips",
      permission: Permissions.PAYROLL.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Payslips");
        updateIconSidebar(e);
      },
    },

    {
      id: "advanceSettlements",
      label: "Advance & Settlements",
      icon: "ri-refund-2-line",
      link: "/#",
      click: function (e: any) {
        e.preventDefault();
        setIsAdvanceSettlements(!isAdvanceSettlements);
        setIscurrentState("Advance & Settlements");
        updateIconSidebar(e);
      },
      stateVariables: isAdvanceSettlements,
      subItems: [
        {
          id: "finance-client-advances",
          label: "Client Advances",
          link: "/finance/client-advances",
          parentId: "advanceSettlements",
          permission: Permissions.CLIENT_ADVANCE.READ,
        },
        {
          id: "finance-client-advance-settlements",
          label: "Client Advance Settlements",
          link: "/finance/client-advance-settlements",
          parentId: "advanceSettlements",
          permission: Permissions.CLIENT_ADVANCE_SETTLEMENT.READ,
        },
        {
          id: "finance-employee-advances",
          label: "Employee Advances",
          link: "/finance/employee-advances",
          parentId: "advanceSettlements",
          permission: Permissions.EMPLOYEE_ADVANCE.READ,
        },
        {
          id: "finance-employee-advance-settlements",
          label: "Employee Advance Settlements",
          link: "/finance/employee-advance-settlements",
          parentId: "advanceSettlements",
          permission: Permissions.EMPLOYEE_ADVANCE_SETTLEMENT.READ,
        },
      ],
    },

    {
      id: "collections",
      label: "Collections",
      icon: "ri-hand-coin-line",
      link: "/finance/payment-expectations",
      permission: Permissions.PAYMENT_EXPECTATION.READ,
      click: function (e: any) {
        e.preventDefault();
        setIscurrentState("Collections");
        updateIconSidebar(e);
      },
    },

    {
      label: "Customers",
      isHeader: true,
    },

    {
      id: "clients",
      label: "Clients",
      icon: "bx bx-user-pin",
      link: "/clients",
      permission: Permissions.CLIENT.READ,
      stateVariables: isClients,
      click: function (e: any) {
        e.preventDefault();
        setIsClients(!isClients);
        setIscurrentState("Clients");
        updateIconSidebar(e);
      },
    },

    {
      label: "System & Administration",
      isHeader: true,
    },

    {
      id: "settings",
      label: "Settings",
      icon: "ri-settings-3-line",
      link: "/settings",
      permission: Permissions.SETTINGS.READ,
    },
    {
      id: "activity-log",
      label: "Activity Log",
      icon: "ri-history-line",
      link: "/activity-log",
      permission: Permissions.ACTIVITY_LOG.READ,
    },
  ];

  const visibleMenuItems = filterMenuByPermission(menuItems, canWithPolicy);

  return <React.Fragment>{visibleMenuItems}</React.Fragment>;
}
